import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon, ChevronDown, ArrowUpRight } from "lucide-react";
import { nav, services, industries, company } from "@/data/site";
import { Icon } from "@/components/common/motion";
import { useTheme } from "@/providers/ThemeProvider";
import { cn } from "@/lib/utils";

export function Logo({ onClick, light = false, className }) {
  return (
    <Link to="/" onClick={onClick} className={cn("flex items-center", className)} data-testid="logo-link" aria-label="NeuroTriq TechLabs home">
      <img
        src={company.logo}
        alt="NeuroTriq TechLabs"
        className="h-11 w-11 rounded-lg bg-white object-contain p-0.5 shadow-sm ring-1 ring-black/5 sm:h-12 sm:w-12"
        loading="eager"
      />
      <span className={cn("ml-2.5 font-display text-lg font-semibold tracking-tight sm:text-xl", light ? "text-white" : "text-foreground")}>
        NeuroTriq<span className="font-normal text-muted-foreground"> TechLabs</span>
      </span>
    </Link>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      aria-label="Toggle theme"
      data-testid="theme-toggle"
      className="relative grid h-9 w-9 place-items-center rounded-full border border-border bg-card text-foreground transition-colors hover:border-primary/50"
    >
      <AnimatePresence mode="wait" initial={false}>
        {theme === "dark" ? (
          <motion.span key="sun" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
            <Sun className="h-4 w-4" />
          </motion.span>
        ) : (
          <motion.span key="moon" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
            <Moon className="h-4 w-4" />
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
}

function MegaMenu() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <NavLink
        to="/services"
        data-testid="nav-services"
        className="flex items-center gap-1 py-2 text-sm font-medium text-foreground/80 transition-colors hover:text-foreground"
      >
        Services <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </NavLink>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 top-full z-50 w-[720px] -translate-x-1/2 pt-4"
          >
            <div className="grid grid-cols-2 gap-2 rounded-2xl border border-border bg-popover p-4 shadow-2xl">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services#${s.slug}`}
                  data-testid={`mega-service-${s.slug}`}
                  className="group flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-muted"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                    <Icon name={s.icon} className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="flex items-center gap-1 text-sm font-semibold">{s.title}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground line-clamp-1">{s.tagline}</span>
                  </span>
                </Link>
              ))}
              <div className="col-span-2 mt-1 flex items-center justify-between rounded-xl bg-secondary p-4 text-secondary-foreground">
                <div>
                  <p className="text-sm font-semibold">Not sure where to start?</p>
                  <p className="text-xs text-white/70">Book a free consultation with our team.</p>
                </div>
                <Link to="/contact" className="flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-xs font-medium text-primary-foreground">
                  Talk to us <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [mobileOpen]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        scrolled ? "border-b border-border glass py-2.5" : "border-b border-transparent py-4"
      )}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
        <Logo />

        <nav className="hidden items-center gap-7 lg:flex">
          <MegaMenu />
          {nav.slice(1).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn("text-sm font-medium transition-colors hover:text-foreground", isActive ? "text-foreground" : "text-foreground/70")
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="hidden rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg sm:inline-flex"
          >
            Start Your Project
          </Link>
          <button
            onClick={() => setMobileOpen((o) => !o)}
            className="grid h-9 w-9 place-items-center rounded-full border border-border bg-card lg:hidden"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-border glass lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-5">
              <NavLink to="/services" className="rounded-lg px-3 py-3 text-base font-medium hover:bg-muted" data-testid="mobile-nav-services">
                Services
              </NavLink>
              {nav.slice(1).map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-lg px-3 py-3 text-base font-medium hover:bg-muted"
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/contact" className="mt-3 rounded-full bg-primary px-5 py-3 text-center text-sm font-medium text-primary-foreground" data-testid="mobile-nav-cta">
                Start Your Project
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
